/**
 * @module  experience/hardgoods
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */

import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
// Interfaces
import { SortRecord } from '@experience/shop/hardgoods/interfaces/browse-hardgood.interface';
import {
  Filter,
  SortOptions,
  FilterOption,
  UserDisplayFilter,
} from '@support/api/common/product.interface';
import { HardgoodsContent } from '@core/aem/interfaces/shop/browse-hardgoods.interface';
// Constants
import { HARDGOODS_CONSTANTS } from '@experience/shop/hardgoods/constants/browse-hardgoods.constants';
// Services
import { SearchResultService } from '@experience/search-results/services/search-result.service';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'tmo-hardgoods-filter-sort',
  templateUrl: './hardgoods-filter-sort.component.html',
  styleUrls: [
    '../../../../../../style-sheets/components/page-titles.component.scss',
    '../../../../../../style-sheets/components/input-checkbox.component.scss',
    '../../../../../../style-sheets/components/input-radio.component.scss',
    '../../../../../../style-sheets/components/html-accordion.component.scss',
    '../../../../../../style-sheets/components/html-filter-sort.component.scss',
  ],
})
export class HardgoodsFilterSortComponent implements OnInit, OnDestroy {
  /**
   * to show filter panel
   */
  @Input() public isFilterPanelShow: boolean;
  /**
   * to show sort panel
   */
  @Input() public isSortPanelShow: boolean;
  /**
   * to store sort categories
   */
  @Input() public sortCategories: SortOptions[];
  /**
   * setter to update category selected Count filters
   */
  @Input() public set updateCategoryFilterCount(value: UserDisplayFilter[]) {
    if (value) {
      this.updateFilterCategoryCount(this.categoryConstantValue);
    }
  }

  /**
   * to update sorted data
   */
  @Output() public updateSortResults: EventEmitter<SortRecord>;
  /**
   * to store filter categories Filter[]
   */
  // tslint:disable
  @Input() public filterCategories: any;
  /**
   * to emit filter applied criterion
   */
  @Output() public filterCriteria: EventEmitter<Filter>;
  /**
   * to emit filter applied criterion
   */
  @Output() public userFilterEmitter: EventEmitter<UserDisplayFilter[]>;
  /**
   * Object Hardgoods configurable data
   */
  @Input() public contentData: HardgoodsContent;
  /**
   * to clear or select radio
   */
  @Input() public selectedSort: string;

  /**
   * boolean value when user on accessory page
   */
  @Input() public isAccessoryPage: boolean;

  /**
   * boolean value when user on accessory page
   */
  @Input() public isAccessoryDetailPage: boolean;

  /**
   * Boolean to show panel
   */
  public panelShow: boolean;

  /**
   * Boolean to show panel
   */
  public priceRangeValue: string;

  /**
   * Boolean to show panel
   */
  public filterText: string;
  /**
   * stores filterOptions values
   */
  public filterOptions: FilterOption[];
  /**
   * Boolean to show panel with filter
   */
  public filterPanelShow: boolean = false;
  /**
   * Boolean to show panel with sort
   */
  public sortPanelShow: boolean = false;
  /**
   * store currently selected  Filter Display
   */
  public selectedFilterDisplayValue: string;
  /**
   * store search term
   */
  public searchTerm: string;
  /**
   * store user filter criteria
   */
  @Input() public userFilter: UserDisplayFilter[];

  /**
   * store hardgood constant value limit to enable the search
   */
  public searchEnableCount: number;
  /**
   * store category filter selection count
   */
  public filterCategorySelectionCount: number;
  /**
   * store category constant value
   */
  public categoryConstantValue: string;
  /**
   * store category max selection limit count
   */
  public categorySelectionLimit: number;
  /**
   * Boolean to track whether to show modal
   */
  private sortDetails: SortRecord;
  /**
   * Boolean to show or hide category limit text
   */
  public showCategoryLimitText: boolean;
  /**
   * holds value of p10m flag
   */
  @Input() public isP10MCustomer: boolean;
  /**
   * Unsubscriber subject
   */
  private ngUnsubscribe: Subject<void>;
  constructor(
    private searchResultService: SearchResultService,
    private objectService: ObjectService
  ) {
    this.sortDetails = {
      sortCriterion: '',
      sortOrder: '',
      sortDisplay: '',
    };
    this.isFilterPanelShow = false;
    this.isSortPanelShow = false;
    this.updateSortResults = new EventEmitter<SortRecord>();
    this.filterCriteria = new EventEmitter<Filter>();
    this.userFilterEmitter = new EventEmitter<UserDisplayFilter[]>();
    this.ngUnsubscribe = new Subject<void>();
    this.selectedFilterDisplayValue = '';
    this.filterCategorySelectionCount = 0;
    this.showCategoryLimitText = true;
    const radix = 10;
    this.userFilter = [];
    this.searchEnableCount = parseInt(HARDGOODS_CONSTANTS.SEARCHENABLECOUNT, radix);
    this.categorySelectionLimit = parseInt(HARDGOODS_CONSTANTS.CATEGORY_SELECTION_LIMIT, radix);
    this.priceRangeValue = HARDGOODS_CONSTANTS.PRICERANGE;
  }

  /**
   * initialies the component
   * @returns void
   */
  public ngOnInit(): void {
    this.filterText = this.contentData.filter;
    this.categoryConstantValue = this.objectService.get(this.contentData, 'category');
    this.searchResultService.sortFilterUpdated$
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe((sortFilterChange: boolean) => {
        this.selectedFilterDisplayValue = '';
        this.panelShow = sortFilterChange;
        this.sortPanelShow = sortFilterChange;
        this.filterPanelShow = sortFilterChange;
      });
  }
  /**
   * Method to destroy service data.
   * @returns void
   */
  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * Method to update FilterText
   * @returns void
   */
  public updateFilterText(): void {
    if (this.filterText === this.contentData.filter) {
      this.filterText = this.contentData.closeFilter;
    } else {
      this.filterText = this.contentData.filter;
    }
    this.clearSearch();
  }
  /**
   * Method to update FilterValue
   * @returns void
   */
  public updateFilterValue(): void {
    if (this.filterText === this.contentData.closeFilter) {
      this.filterText = this.contentData.filter;
    }
  }

  /**
   * Method to search filter criteria.
   * @returns void
   */
  public search(idx: number): void {
    let term = this.searchTerm;
    const filterTerms = this.objectService.get(this.filterCategories, `${idx}.filterOptions`);
    this.filterCategories[idx].filteredData = filterTerms.filter((filterTerm: FilterOption) => {
      return (
        filterTerm.value.toLowerCase().indexOf(term.toLowerCase()) >= 0 || filterTerm.isSelected
      );
    });
  }

  /**
   * Method to clear Search data.
   * @returns void
   */
  public clearSearch(): void {
    this.searchTerm = null;
    for (let i: number = 0; i < this.filterCategories.length; i++) {
      this.filterCategories[i].filteredData = null;
    }
  }

  /**
   * Handle the selection of filter
   * @param selectedValue value of the filter criteria
   * @param optionValue option value
   * @param selectedFilterDisplayValue selected Filter Display Value
   * @returns void
   */
  public selectedValue(
    option: FilterOption,
    optionValue?: string,
    selectedFilterDisplayValue?: string
  ): void {
    const filter = {
      type: option.displayName,
      optionValue: optionValue,
      filterType: selectedFilterDisplayValue,
    };
    const index = this.userFilter.findIndex((userDisplayFilter: UserDisplayFilter) => {
      return userDisplayFilter.optionValue === filter.optionValue;
    });
    if (index > -1) {
      this.userFilter.splice(index, 1);
    } else {
      this.userFilter.push(filter);
    }
    this.setIsSelectedValue(option, optionValue, selectedFilterDisplayValue);
    this.selectedFilterDisplayValue = selectedFilterDisplayValue;
    this.searchResultService.setSelectedFilterPath(optionValue);
    this.filterCriteria.emit(this.filterCategories);
    this.userFilterEmitter.emit(this.userFilter);
    this.updateFilterCategoryCount(selectedFilterDisplayValue);
  }

  /**
   * Handle the selection of filter category count
   * @param categoryText value of the category text
   * @returns void
   */
  public updateFilterCategoryCount(paramCategory: string): void {
    const categoryText = (paramCategory || '').toLocaleLowerCase();
    if (
      this.isAccessoryPage &&
      !this.isAccessoryDetailPage &&
      categoryText === this.categoryConstantValue &&
      this.filterCategories
    ) {
      const categoryFilters = this.filterCategories.filter((filter: Filter) => {
        return this.objectService.get(filter, 'fieldName', '').toLocaleLowerCase() === categoryText;
      });

      const filterOptions = this.objectService.get(categoryFilters, '0.filterOptions');
      if (filterOptions) {
        const selectedFilterOptions = filterOptions.filter((filterOption: FilterOption) => {
          return filterOption.isSelected;
        });
        this.filterCategorySelectionCount = selectedFilterOptions.length;
      }
    }
  }

  /**
   * Handle the sorting of data
   * @param sortCriterion criterio of the sort applied
   * @param sortValue value of the sort applied (asc/desc)
   * @returns void
   */
  public applySort(sortCriterion: string, sortValue: string): void {
    this.sortDetails.sortOrder =
      sortCriterion === HARDGOODS_CONSTANTS.PRICE_HIGH_TO_LOW ||
      sortCriterion === HARDGOODS_CONSTANTS.Z_TO_A
        ? HARDGOODS_CONSTANTS.DESCENDING
        : HARDGOODS_CONSTANTS.ASCENDING;
    this.searchResultService.setSelectedFilterPath(sortValue);
    this.sortDetails.sortCriterion = sortValue;
    this.sortDetails.sortDisplay = sortCriterion;
    this.updateSortResults.emit(this.sortDetails);
    this.filterPanelShow = false;
  }

  /**
   * To show category selection text and apply class on it
   */
  public onTabClose(index: number) {
    if (this.filterCategories[index].displayName.toLowerCase() === this.categoryConstantValue)
      this.showCategoryLimitText = true;
  }

  /**
   * To show category selection text and apply class on it
   */
  public onTabOpen(index: number) {
    if (this.filterCategories[index].displayName.toLowerCase() === this.categoryConstantValue)
      this.showCategoryLimitText = false;
  }

  /**
   * sets isSelectedValue
   * @returns void
   */
  private setIsSelectedValue(
    option: FilterOption,
    optionValue?: string,
    selectedFilterDisplayValue?: string
  ): void {
    let selectedFilterField;
    const selectedCategories = this.filterCategories.find((filterCategory: any) => {
      return filterCategory.fieldName === selectedFilterDisplayValue;
    });
    if (selectedCategories && selectedCategories.filterOptions) {
      selectedFilterField = selectedCategories.filterOptions.find(
        (selectedCategory: FilterOption) => {
          return selectedCategory.value === optionValue;
        }
      );
    }
    if (selectedFilterField && !selectedFilterField.isSelected) {
      selectedFilterField.isSelected =
        (option.isSelected === undefined || option.isSelected === false) &&
        this.userFilter.findIndex((userDisplayFilter: UserDisplayFilter) => {
          return userDisplayFilter.optionValue === optionValue;
        }) > -1;
    }
  }
}
